import React, { useState, useEffect } from 'react';
import { Text, Button, Group, Textarea, Tooltip, Alert, Loader, UnstyledButton, Checkbox, Collapse } from '@mantine/core';
import { IconAlertCircle, IconChevronDown, IconCircleCheck, IconMessage } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ACHIEVEMENTS } from './achievements';

const COMMENT_MAX = 2000; // как LESSON_COMMENT_MAX на бэкенде

// Урок уже отмечали: панель показываем свёрнутой, раскрыть можно по клику
const isLessonMarked = (data) => data.status === 'Held' || Boolean(data.comment) || Boolean(data.achievement);

// Панель в карточке начавшегося урока: галочка «Урок состоялся», комментарий ученику и одно достижение за урок.
// Все записи идут через /api/teacher/:teacherId/lesson/:callId — ученика бэкенд берёт из урока сам.
function LessonMarkPanel({ apiBaseUrl, teacherId, lessonId }) {
  const { t } = useTranslation();
  const [state, setState] = useState(null); // { status, comment, achievement, canMark } с бэкенда
  const [comment, setComment] = useState('');
  const [held, setHeld] = useState(false); // галочка «Урок состоялся»
  const [selected, setSelected] = useState(null); // ключ достижения, которое учитель хочет выдать
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const lessonUrl = `${apiBaseUrl}/teacher/${teacherId}/lesson/${lessonId}`;

  useEffect(() => {
    let cancelled = false;
    fetch(lessonUrl, { credentials: 'include' })
      .then((response) => {
        if (!response.ok) throw new Error(`Ошибка HTTP: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setState(data);
        setComment(data.comment || '');
        setHeld(data.status === 'Held');
        setExpanded(!isLessonMarked(data));
      })
      .catch((err) => {
        console.error('Ошибка при загрузке урока:', err);
        if (!cancelled) setLoadError(true);
      });
    return () => { cancelled = true; };
  }, [lessonUrl]);

  useEffect(() => {
    if (!justSaved) return undefined;
    const timer = setTimeout(() => setJustSaved(false), 3000);
    return () => clearTimeout(timer);
  }, [justSaved]);

  if (loadError) {
    return <Alert color="red" icon={<IconAlertCircle size={18} />}>{t('lessonMark.loadError')}</Alert>;
  }
  if (!state) {
    return <Group justify="center" py="md"><Loader size="sm" /></Group>;
  }

  const heldChanged = held !== (state.status === 'Held');
  const commentChanged = comment.trim() !== (state.comment || '').trim();
  const canSubmit = state.canMark && (heldChanged || commentChanged || selected);
  const awarded = state.achievement && ACHIEVEMENTS.find((a) => a.en === state.achievement);

  const send = (url, method, body) => fetch(url, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    setJustSaved(false);
    try {
      const body = {};
      if (heldChanged) body.held = held;
      if (commentChanged) body.comment = comment;

      if (Object.keys(body).length > 0) {
        const response = await send(lessonUrl, 'PUT', body);
        if (!response.ok) throw new Error(`Ошибка HTTP: ${response.status}`);
        const saved = await response.json();
        setState((prev) => ({ ...prev, status: saved.status, comment: saved.comment }));
        setComment(saved.comment);
        setHeld(saved.status === 'Held');
      }

      if (selected) {
        const response = await send(`${lessonUrl}/achievement`, 'POST', { achievement: selected });
        if (!response.ok) {
          // Урок уже отмечен — не теряем это, показываем ошибку только про достижение
          setError(response.status === 409 ? t('lessonMark.achievementTaken') : t('lessonMark.achievementError'));
          return;
        }
        setState((prev) => ({ ...prev, achievement: selected }));
        setSelected(null);
      }

      setJustSaved(true);
      setExpanded(false);
    } catch (err) {
      console.error('Ошибка при отметке урока:', err);
      setError(t('lessonMark.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    setError(null);
    try {
      const response = await send(`${lessonUrl}/achievement`, 'DELETE');
      if (!response.ok) throw new Error(`Ошибка HTTP: ${response.status}`);
      setState((prev) => ({ ...prev, achievement: null }));
    } catch (err) {
      console.error('Ошибка при отмене достижения:', err);
      setError(t('lessonMark.revokeError'));
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="lesson-mark">
      <UnstyledButton
        className="lesson-mark-header"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap="md" wrap="wrap" style={{ minWidth: 0 }}>
            <Text fw={700}>{t('lessonMark.title')}</Text>
            {!expanded && (
              // Сводка сохранённого состояния, пока панель свёрнута
              <Group gap="md" wrap="wrap" style={{ minWidth: 0 }}>
                {state.status === 'Held' ? (
                  <Group gap={6} c="#10B981" wrap="nowrap">
                    <IconCircleCheck size={18} />
                    <Text size="sm" fw={600}>{t('lessonMark.held')}</Text>
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">{t('lessonMark.notHeld')}</Text>
                )}
                {state.achievement && (
                  <Group gap={6} wrap="nowrap">
                    {awarded && <img src={awarded.image} alt="" width={24} height={24} />}
                    <Text size="sm" fw={600} c="#8B5CF6">{awarded ? t(awarded.title) : state.achievement}</Text>
                  </Group>
                )}
                {state.comment && (
                  <Group gap={6} wrap="nowrap" style={{ minWidth: 0, maxWidth: 360 }}>
                    <IconMessage size={18} color="var(--text-muted)" style={{ flexShrink: 0 }} />
                    <Text size="sm" c="var(--text-secondary)" truncate="end">{state.comment}</Text>
                  </Group>
                )}
              </Group>
            )}
          </Group>
          <Group gap="sm" wrap="nowrap">
            {justSaved && <Text size="sm" fw={600} c="#10B981">{t('lessonMark.saved')}</Text>}
            <IconChevronDown size={20} className={`lesson-mark-chevron ${expanded ? 'open' : ''}`} />
          </Group>
        </Group>
      </UnstyledButton>

      <Collapse expanded={expanded}>
        <div className="lesson-mark-body">
          {!state.canMark && (
            <Alert color="yellow" mb="md" icon={<IconAlertCircle size={18} />}>{t('lessonMark.notMarkable')}</Alert>
          )}

          <Checkbox
            mb="md"
            size="md"
            color="#10B981"
            label={t('lessonMark.held')}
            description={t('lessonMark.heldHint')}
            checked={held}
            onChange={(e) => setHeld(e.currentTarget.checked)}
            disabled={!state.canMark}
          />

          <div className="lesson-mark-fields">
            <Textarea
              label={t('lessonMark.comment')}
              placeholder={t('lessonMark.commentPlaceholder')}
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
              maxLength={COMMENT_MAX}
              autosize
              minRows={4}
              maxRows={10}
              disabled={!state.canMark}
            />

            <div>
              <Text fw={500} size="sm">{t('lessonMark.achievement')}</Text>
              <Text size="xs" c="dimmed" mb="xs">{t('lessonMark.achievementHint')}</Text>

              {awarded ? (
                <Group justify="space-between" wrap="nowrap" className="lesson-mark-awarded">
                  <Group gap="sm" wrap="nowrap">
                    <img src={awarded.image} alt="" width={48} height={48} />
                    <Text size="sm" fw={600}>{t('lessonMark.achievementAwarded', { title: t(awarded.title) })}</Text>
                  </Group>
                  {state.canMark && (
                    <Button variant="subtle" color="red" size="xs" onClick={handleRevoke} loading={revoking}>
                      {t('lessonMark.revoke')}
                    </Button>
                  )}
                </Group>
              ) : state.achievement ? (
                // Ключ из CRM, которого ещё нет в справочнике кабинета
                <Text size="sm" fw={600}>{t('lessonMark.achievementAwarded', { title: state.achievement })}</Text>
              ) : (
                <>
                  <div className="lesson-mark-grid" role="radiogroup" aria-label={t('lessonMark.achievement')}>
                    {ACHIEVEMENTS.map((achievement) => (
                      <Tooltip
                        key={achievement.en}
                        label={
                          <div>
                            <Text size="xs" fw={700}>{t(achievement.title)}</Text>
                            <Text size="xs">{t(achievement.hint)}</Text>
                          </div>
                        }
                        withArrow
                        multiline
                        w={220}
                      >
                        <UnstyledButton
                          role="radio"
                          aria-checked={selected === achievement.en}
                          aria-label={t(achievement.title)}
                          disabled={!state.canMark}
                          className={`lesson-mark-option ${selected === achievement.en ? 'selected' : ''}`}
                          onClick={() => setSelected(selected === achievement.en ? null : achievement.en)}
                        >
                          <img src={achievement.image} alt="" />
                        </UnstyledButton>
                      </Tooltip>
                    ))}
                  </div>
                  {selected && (
                    <Text size="sm" mt="xs" fw={600} c="#8B5CF6">
                      {t(ACHIEVEMENTS.find((a) => a.en === selected).title)}
                    </Text>
                  )}
                </>
              )}
            </div>
          </div>

          {error && <Alert color="red" mt="md" icon={<IconAlertCircle size={18} />}>{error}</Alert>}

          {state.canMark && (
            <Group justify="flex-end" align="center" mt="md" gap="md">
              <Button
                className="gradient-btn lesson-mark-submit"
                onClick={handleSubmit}
                loading={saving}
                disabled={!canSubmit}
              >
                {t('lessonMark.save')}
              </Button>
            </Group>
          )}
        </div>
      </Collapse>

      <style>
        {`
          .lesson-mark {
            margin-top: 20px;
            padding-top: 20px;
            border-top: 1px solid rgba(14, 165, 233, 0.15);
          }
          .lesson-mark-header {
            display: block;
            width: 100%;
            text-align: left;
          }
          .lesson-mark-chevron {
            color: var(--text-muted);
            transition: transform 0.2s ease;
          }
          .lesson-mark-chevron.open {
            transform: rotate(180deg);
          }
          .lesson-mark-body {
            padding-top: 16px;
          }
          .lesson-mark-fields {
            display: grid;
            grid-template-columns: 1fr;
            gap: 20px;
          }
          @media (min-width: 768px) {
            .lesson-mark-fields {
              grid-template-columns: 1fr 1fr;
            }
          }
          .lesson-mark-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(52px, 1fr));
            gap: 8px;
          }
          .lesson-mark-option {
            aspect-ratio: 1;
            padding: 6px;
            border-radius: 16px;
            border: 2px solid transparent;
            background: #f0f9ff;
            transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
          }
          .lesson-mark-option img {
            width: 100%;
            height: 100%;
            display: block;
          }
          .lesson-mark-option:hover:not(:disabled) {
            background: #e0f2fe;
            transform: translateY(-1px);
          }
          .lesson-mark-option.selected {
            border-color: #8B5CF6;
            background: rgba(139, 92, 246, 0.1);
          }
          .lesson-mark-option:disabled {
            opacity: 0.4;
            cursor: not-allowed;
          }
          .lesson-mark-option:focus-visible {
            outline: 2px solid #0EA5E9;
            outline-offset: 2px;
          }
          /* .gradient-btn красит фон через !important — без этого неактивная кнопка выглядит активной */
          .lesson-mark-submit[data-disabled] {
            opacity: 0.45;
            box-shadow: none;
            cursor: not-allowed;
          }
          .lesson-mark-awarded {
            padding: 8px 12px;
            border-radius: 16px;
            background: rgba(139, 92, 246, 0.08);
          }
        `}
      </style>
    </div>
  );
}

export default LessonMarkPanel;
